import type { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { Stack } from '@mui/material';
import { MHMTypography } from '../MHM/MHMTypography';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { companyDeviceSettings } from '../../../models/interfaces/company_interfaces';
import {
  changeCompanyDeviceAtom,
  originalCompanyDeviceAtom,
} from '../../../models/atoms/company_atoms';
import { unitConverterMap } from '../../../utils/helper_functions';

type Props = {
  title: ReactNode;
  messageTimeKey: keyof companyDeviceSettings;
  messageTimeUnit: 'hours' | 'days';
  measurementTimeKey: keyof companyDeviceSettings;
  measurementTimeUnit: 'minutes' | 'hours';
};

export function ChangedFrequency(props: Props) {
  const {
    title,
    messageTimeKey,
    messageTimeUnit,
    measurementTimeKey,
    measurementTimeUnit,
  } = props;
  const { t } = useTranslation();
  const changed = useRecoilValue(changeCompanyDeviceAtom);
  const original = useRecoilValue(originalCompanyDeviceAtom);

  const unitDisplayMap = {
    hours: t('Hours'),
    days: t('Days'),
    minutes: t('Minutes'),
  } as const;

  if (
    !changed ||
    !original ||
    (original[messageTimeKey] === changed[messageTimeKey] &&
      original[measurementTimeKey] === changed[measurementTimeKey])
  ) {
    return null;
  }

  return (
    <div>
      <MHMTypography medium textAlign="center">
        {title}
      </MHMTypography>
      {original[messageTimeKey] === changed[messageTimeKey] ? null : (
        <>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap="0 0.5rem"
            flexWrap="wrap"
          >
            <MHMTypography>{t('Relay Frequency')}:</MHMTypography>
            <MHMTypography>
              {unitConverterMap[messageTimeUnit](original[messageTimeKey])}
              &nbsp; →&nbsp;
              {unitConverterMap[messageTimeUnit](changed[messageTimeKey])}&nbsp;
              {unitDisplayMap[messageTimeUnit]}
            </MHMTypography>
          </Stack>
        </>
      )}
      {original[measurementTimeKey] === changed[measurementTimeKey] ? null : (
        <>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap="0 0.5rem"
            flexWrap="wrap"
          >
            <MHMTypography>{t('Sensor Reading Frequency')}:</MHMTypography>
            <MHMTypography>
              {unitConverterMap[measurementTimeUnit](
                original[measurementTimeKey],
              )}
              &nbsp; →&nbsp;
              {unitConverterMap[measurementTimeUnit](
                changed[measurementTimeKey],
              )}
              &nbsp;
              {unitDisplayMap[measurementTimeUnit]}
            </MHMTypography>
          </Stack>
        </>
      )}
    </div>
  );
}
